import { Link, useNavigate, useLocation } from "react-router-dom";
import { Heart, ShoppingBag, Search, User, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Panties", to: "/shop?category=Panties" },
  { label: "Camisoles", to: "/shop?category=Camisoles" },
  { label: "Kids", to: "/shop?audience=Kids" },
  { label: "Wholesale", to: "/wholesale" },
];

export const Navbar = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { count } = useCart();
  const { count: wcount } = useWishlist();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const isHome = loc.pathname === "/";
  const transparent = isHome && !scrolled && !mobile;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    setSearchOpen(false);
    nav(`/shop?search=${encodeURIComponent(q)}`);
  };

  const txt = transparent ? "text-white" : "text-foreground";

  return (
    <>
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${transparent ? "bg-transparent" : "bg-[hsl(var(--ivory))]/95 backdrop-blur-md border-b border-border"}`}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="h-[70px] flex items-center gap-6">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-btn" aria-label="Menu">
            <Menu className={`w-6 h-6 ${txt}`} />
          </button>

          <Link to="/" className="lg:flex-none" data-testid="brand-logo">
            <span className={`font-logo text-xl sm:text-2xl tracking-[0.18em] ${txt}`}>BODYCRUSH<span className="hidden sm:inline text-[10px] align-top tracking-normal ml-1.5 opacity-70">by Gujarish</span></span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 mx-auto">
            {links.map((l) => (
              <Link key={l.label} to={l.to} className={`bc-underline text-xs uppercase tracking-[0.18em] font-normal ${txt} opacity-90 hover:opacity-100 transition-opacity`} data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-5 ml-auto lg:ml-0">
            <button onClick={() => setSearchOpen((s) => !s)} className={txt} data-testid="search-toggle" aria-label="Search"><Search className="w-[18px] h-[18px]" /></button>
            <Link to="/wishlist" className={`relative ${txt}`} data-testid="wishlist-link" aria-label="Wishlist">
              <Heart className="w-[18px] h-[18px]" />
              {wcount > 0 && <Dot n={wcount} testid="wishlist-count" />}
            </Link>
            <Link to={user ? "/account" : "/login"} className={txt} data-testid="account-link" aria-label="Account"><User className="w-[18px] h-[18px]" /></Link>
            <Link to="/cart" className={`relative ${txt}`} data-testid="cart-link" aria-label="Cart">
              <ShoppingBag className="w-[18px] h-[18px]" />
              {count > 0 && <Dot n={count} testid="cart-count" />}
            </Link>
          </div>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="bg-[hsl(var(--ivory))] border-b border-border bc-fade">
          <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto px-8 h-16 flex items-center gap-3">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search styles, codes…" className="flex-1 bg-transparent outline-none text-base" data-testid="search-input" />
            <button type="button" onClick={() => setSearchOpen(false)}><X className="w-5 h-5 text-muted-foreground" /></button>
          </form>
        </div>
      )}

    </header>

      {/* Mobile drawer - outside header to avoid positioning issues */}
      {mobile && (
        <div 
          className="lg:hidden"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
          }}
          data-testid="mobile-menu"
        >
          {/* Overlay */}
          <div 
            onClick={() => setMobile(false)} 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
            }}
            data-testid="mobile-menu-overlay" 
          />
          {/* Drawer */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: '280px',
              backgroundColor: '#ffffff',
              boxShadow: '4px 0 20px rgba(0,0,0,0.15)',
              padding: '24px',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
              <span className="font-logo" style={{ fontSize: '18px', letterSpacing: '0.18em', color: '#1a1a1a' }}>BODYCRUSH</span>
              <button 
                onClick={() => setMobile(false)} 
                style={{ padding: '8px', borderRadius: '50%', background: 'transparent', border: 'none', cursor: 'pointer' }}
                data-testid="mobile-menu-close"
              >
                <X style={{ width: '24px', height: '24px', color: '#1a1a1a' }} />
              </button>
            </div>
            <nav style={{ display: 'flex', flexDirection: 'column' }}>
              {links.map((l) => (
                <Link 
                  key={l.label} 
                  to={l.to} 
                  onClick={() => setMobile(false)} 
                  style={{
                    padding: '16px 8px',
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                    color: '#1a1a1a',
                    borderBottom: '1px solid #e5e5e5',
                    textDecoration: 'none',
                  }}
                  data-testid={`mnav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e5e5' }}>
              <Link to="/account" onClick={() => setMobile(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 8px', fontSize: '14px', color: '#666', textDecoration: 'none' }}>
                <User style={{ width: '20px', height: '20px' }} /> My Account
              </Link>
              <Link to="/wishlist" onClick={() => setMobile(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 8px', fontSize: '14px', color: '#666', textDecoration: 'none' }}>
                <Heart style={{ width: '20px', height: '20px' }} /> Wishlist {wcount > 0 && `(${wcount})`}
              </Link>
              <Link to="/cart" onClick={() => setMobile(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 8px', fontSize: '14px', color: '#666', textDecoration: 'none' }}>
                <ShoppingBag style={{ width: '20px', height: '20px' }} /> Cart {count > 0 && `(${count})`}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Dot = ({ n, testid }) => (
  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[9px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-sans" data-testid={testid}>{n}</span>
);
